import { useEffect, useRef } from "react";

import {
  getCatalogDecorationUrl,
  isMapNotePinBanner,
  MAP_NOTE_PIN_COLOR_LABEL,
  MAP_NOTE_PIN_COLORS,
  MAP_NOTE_PIN_HEX_COLORS,
  MAP_NOTE_PIN_OVERLAY_ID,
  type MapNotePinColor,
  type MapNotePinColorOnly,
} from "@west-marches/shared";

import { MapNoteMarkerPicker } from "@/components/MapNoteMarkerPicker";
import { getMapChromeTheme } from "@/components/mapChromeTheme";
import { useMapNoteTemplateListQuery } from "@/lib/queries/mapNoteTemplateQueries";
import { useEditorStore } from "@/state/useEditorStore";
import { useMapNoteStore } from "@/state/useMapNoteStore";
import { useMapNoteTemplateManagerStore } from "@/state/useMapNoteTemplateManagerStore";

const HIDDEN_OPACITY = 0.25;
const NO_MAP_MARKER_LABEL = "No map marker";

export function MapNoteToolPanel() {
  const theme = getMapChromeTheme();
  const activeTool = useEditorStore((s) => s.activeTool);
  const pendingPinColor = useMapNoteStore((s) => s.pendingPinColor);
  const setPendingPinColor = useMapNoteStore((s) => s.setPendingPinColor);
  const pendingHidden = useMapNoteStore((s) => s.pendingHidden);
  const setPendingHidden = useMapNoteStore((s) => s.setPendingHidden);
  const pendingTemplateId = useMapNoteStore((s) => s.pendingTemplateId);
  const setPendingTemplateId = useMapNoteStore((s) => s.setPendingTemplateId);
  const openManager = useMapNoteTemplateManagerStore((s) => s.openManager);

  const isActive = activeTool === "map-note";
  const { data: templates = [] } = useMapNoteTemplateListQuery(isActive);

  const selectedTemplate = pendingTemplateId
    ? templates.find((t) => t.id === pendingTemplateId) ?? null
    : null;
  const selectedTemplateColor = selectedTemplate?.color ?? null;
  const selectedTemplateHidden = selectedTemplate?.hidden ?? null;

  // When a template is selected (or its marker/hidden state changes), apply
  // those defaults to the in-memory drop preferences. The user can still
  // freely override after this fires; we only resync when the template id
  // changes or when the underlying template's saved values change.
  const lastAppliedTemplateRef = useRef<{
    id: string | null;
    color: MapNotePinColor | null;
    hidden: boolean | null;
  }>({ id: null, color: null, hidden: null });
  useEffect(() => {
    if (!selectedTemplate) {
      lastAppliedTemplateRef.current = { id: null, color: null, hidden: null };
      return;
    }
    const last = lastAppliedTemplateRef.current;
    const same =
      last.id === selectedTemplate.id &&
      last.color === selectedTemplate.color &&
      last.hidden === selectedTemplate.hidden;
    if (same) return;
    lastAppliedTemplateRef.current = {
      id: selectedTemplate.id,
      color: selectedTemplate.color,
      hidden: selectedTemplate.hidden,
    };
    setPendingPinColor(selectedTemplate.color);
    setPendingHidden(selectedTemplate.hidden);
  }, [
    selectedTemplate,
    selectedTemplateColor,
    selectedTemplateHidden,
    setPendingPinColor,
    setPendingHidden,
  ]);

  if (!isActive) return null;

  const handleTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setPendingTemplateId(value === "" ? null : value);
  };

  const hiddenSwatchPinUrl = getCatalogDecorationUrl(MAP_NOTE_PIN_OVERLAY_ID[pendingPinColor]);
  const hiddenSwatchHex =
    MAP_NOTE_PIN_HEX_COLORS[pendingPinColor as MapNotePinColorOnly] ?? "#cccccc";
  const isBannerSelected = isMapNotePinBanner(pendingPinColor);

  return (
    <div
      data-map-note-tool-panel
      className={`fixed bottom-28 left-1/2 z-20 flex w-max max-w-[min(92vw,720px)] flex-col gap-2 -translate-x-1/2 px-2.5 py-2 ${theme.floatingPanel}`}
    >
      <div className={`text-[10px] tracking-[0.2em] ${theme.textSubtle}`}>Marker</div>

      <div className="flex flex-wrap items-center gap-1.5">
        {MAP_NOTE_PIN_COLORS.map((color) => {
          const selected = !pendingHidden && pendingPinColor === color;
          return (
            <button
              key={color}
              type="button"
              onClick={() => {
                if (pendingHidden) setPendingHidden(false);
                setPendingPinColor(color);
              }}
              aria-label={MAP_NOTE_PIN_COLOR_LABEL[color]}
              aria-pressed={selected}
              title={MAP_NOTE_PIN_COLOR_LABEL[color]}
              className={`h-5 w-5 rounded-full border-2 transition-transform ${
                selected
                  ? "scale-110 border-neutral-950"
                  : "border-neutral-950/30 hover:border-neutral-950/60"
              }`}
              style={{ backgroundColor: MAP_NOTE_PIN_HEX_COLORS[color] }}
            />
          );
        })}
        <button
          type="button"
          onClick={() => setPendingHidden(true)}
          aria-label={pendingHidden ? `${NO_MAP_MARKER_LABEL} (selected)` : NO_MAP_MARKER_LABEL}
          aria-pressed={pendingHidden}
          title={NO_MAP_MARKER_LABEL}
          className={`relative inline-flex h-5 w-5 items-center justify-center rounded-full border-2 transition-transform overflow-hidden ${
            pendingHidden
              ? "scale-110 border-neutral-950"
              : "border-neutral-950/30 hover:border-neutral-950/60"
          }`}
          style={{ backgroundColor: hiddenSwatchHex }}
        >
          {hiddenSwatchPinUrl ? (
            <img
              src={hiddenSwatchPinUrl}
              alt=""
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                opacity: HIDDEN_OPACITY,
              }}
            />
          ) : (
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                opacity: HIDDEN_OPACITY,
                backgroundColor: hiddenSwatchHex,
              }}
            />
          )}
          <span className="relative z-10 rounded-[4px] bg-[#fffaf1]/92 px-px text-[6px] leading-none font-black uppercase tracking-[-0.04em] text-neutral-950/80">
            NONE
          </span>
        </button>

        <MapNoteMarkerPicker
          value={pendingPinColor}
          onChange={(marker) => {
            if (pendingHidden) setPendingHidden(false);
            setPendingPinColor(marker);
          }}
          triggerMarker={isBannerSelected ? pendingPinColor : "banner"}
          triggerTitle="Pick marker"
          triggerLabel={`Pick marker. Current: ${MAP_NOTE_PIN_COLOR_LABEL[pendingPinColor]}`}
          className="ml-1"
        />
      </div>

      <div className={`mt-1 text-[10px] tracking-[0.2em] ${theme.textSubtle}`}>Template</div>
      <div className="flex items-center gap-2">
        <select
          aria-label="Template applied to next pin"
          value={pendingTemplateId ?? ""}
          onChange={handleTemplateChange}
          className="min-w-[160px] rounded-md border-2 border-neutral-950/15 bg-[#fffaf1] px-2 py-1 text-xs text-neutral-950 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3e664d]"
        >
          <option value="">No template</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.name || "Untitled template"}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => openManager(pendingTemplateId)}
          className="rounded-md border-2 border-neutral-950/15 bg-[#fff2d9]/72 px-2 py-1 text-xs font-medium text-neutral-950 hover:bg-[#ffe7bc]"
          title="Open the template manager"
        >
          Manage
        </button>
      </div>
    </div>
  );
}
